import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller, FieldValues } from "react-hook-form";
import {
  TextField,
  Button,
  CardContent,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import "./device-keys-management-form.scss";
import { deviceTypeApi } from "../../api/device-types/device-type.service";
import { Customer, DeviceType } from "../../../types";
import { customerApi } from "../../api/customer/customer.service";

const { useGetDeviceTypesQuery } = deviceTypeApi;

// Define the type for the form data
export type DeviceKeyManagementFormData = {
  alias: string;
  deviceType: DeviceType;
  key: string;
  customerId: string;
  customer: Customer;
  ranges: Array<{
    cmin: string;
    cmax: string;
    wmin: string;
    wmax: string;
    unit: string;
  }>;
};

interface Props {
  defaultValues: FieldValues;
  onSubmit: (form_values: DeviceKeyManagementFormData) => void;
  editMode?: boolean;
}
const DeviceKeyManagementForm: React.FC<Props> = ({ defaultValues, onSubmit, editMode = false }) => {
  const { data: customer_list = [], isFetching: is_fetching_customers } = customerApi.useGetCustomersQuery();
  const { data: device_types = [], isFetching } = useGetDeviceTypesQuery();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<DeviceKeyManagementFormData>({
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ranges",
    rules: {
      minLength: 1,
    },
  });

  useEffect(() => {
    // Ensure at least one range is always present
    if (fields.length === 0) {
      setValue("ranges", [
        {
          cmin: "",
          cmax: "",
          wmin: "",
          wmax: "",
          unit: "",
        },
      ]);
    }
  }, [fields, setValue]);

  const addRangeRow = () => {
    append({
      cmin: "",
      cmax: "",
      wmin: "",
      wmax: "",
      unit: "",
    });
  };

  return (
    <div className="dynamic-ranges-form">
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="form-container">
          {/* Main Form Fields */}
          <div className="main-fields-grid">
            <Controller
              name="deviceType"
              control={control}
              rules={{ required: "Device Type is required" }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={device_types}
                  loading={isFetching}
                  disabled={editMode}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.type === value?.type}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Device Type"
                      variant="outlined"
                      error={!!errors.deviceType}
                      helperText={errors.deviceType?.message}
                      fullWidth
                    />
                  )}
                  value={value}
                  onChange={(_, newValue) => onChange(newValue)}
                />
              )}
            />
            <Controller
              name="customer"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={customer_list}
                  loading={is_fetching_customers}
                  disabled={editMode}
                  getOptionLabel={(option) => option.customerTitle ?? ""}
                  isOptionEqualToValue={(option, value) => option.customerId === value?.customerId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      variant="outlined"
                      error={!!errors.deviceType}
                      helperText={errors.deviceType?.message}
                      fullWidth
                    />
                  )}
                  value={value}
                  onChange={(_, newValue) => onChange(newValue)}
                />
              )}
            />
            <TextField
              {...register("key", { required: "Key is required" })}
              label="Key"
              variant="outlined"
              fullWidth
              disabled={editMode}
              error={!!errors.key}
              helperText={errors.key?.message}
            />
            <TextField
              {...register("alias", { required: "Alias is required" })}
              label="Alias"
              variant="outlined"
              fullWidth
              error={!!errors.alias}
              helperText={errors.alias?.message}
            />
          </div>

          {/* Ranges Section */}
          <div className="ranges-section">
            <div className="ranges-header">
              <Typography variant="h6">Ranges</Typography>
              <Button variant="outlined" onClick={addRangeRow} startIcon={<AddIcon />}>
                Add Range
              </Button>
            </div>

            {/* Ranges Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Critical Min</TableCell>
                    <TableCell>Critical Max</TableCell>
                    <TableCell>Warning Min</TableCell>
                    <TableCell>Warning Max</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        <TextField
                          {...register(`ranges.${index}.cmin` as const, {
                            required: "Critical Min is required",
                          })}
                          variant="outlined"
                          fullWidth
                          placeholder="Critical Min"
                          size="small"
                          error={!!errors.ranges?.[index]?.cmin}
                          helperText={errors.ranges?.[index]?.cmin?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          {...register(`ranges.${index}.cmax` as const, {
                            required: "Critical Max is required",
                          })}
                          variant="outlined"
                          fullWidth
                          placeholder="Critical Max"
                          size="small"
                          error={!!errors.ranges?.[index]?.cmax}
                          helperText={errors.ranges?.[index]?.cmax?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          {...register(`ranges.${index}.wmin` as const, {
                            required: "Warning Min is required",
                          })}
                          variant="outlined"
                          fullWidth
                          placeholder="Warning Min"
                          size="small"
                          error={!!errors.ranges?.[index]?.wmin}
                          helperText={errors.ranges?.[index]?.wmin?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          {...register(`ranges.${index}.wmax` as const, {
                            required: "Warning Max is required",
                          })}
                          variant="outlined"
                          fullWidth
                          placeholder="Warning Max"
                          size="small"
                          error={!!errors.ranges?.[index]?.wmax}
                          helperText={errors.ranges?.[index]?.wmax?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          {...register(`ranges.${index}.unit` as const, {
                            required: "Unit is required",
                          })}
                          variant="outlined"
                          fullWidth
                          placeholder="Unit"
                          size="small"
                          error={!!errors.ranges?.[index]?.unit}
                          helperText={errors.ranges?.[index]?.unit?.message}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {fields.length > 1 && (
                          <IconButton onClick={() => remove(index)} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Submit Button */}
          <div className="submit-section">
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Submit
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  );
};

export default DeviceKeyManagementForm;
